/**
 * The part of the phone library (intl-tel-input utils) this file relies on.
 */
export interface PhoneNumberUtils {
  isValidNumber(number: string, iso2?: string): boolean;
  getNumberType(number: string, iso2?: string): number;
  formatNumber(
    number: string,
    iso2: string | undefined,
    format?: number,
  ): string;
  numberType: Record<string, number>;
  numberFormat: Record<string, number>;
}

export interface ResolvedPhone {
  // country the number belongs to
  iso2: string;
  e164: string;
}

interface ResolveOptions {
  // what is in the input right now, exactly as typed
  raw: string;
  // country currently selected in the field
  selected?: string;
  // countries a number typed without a country code may belong to
  candidates: string[];
  // iso2 -> dial code ("sa" -> "966")
  dialCodes: Record<string, string>;
  utils: PhoneNumberUtils;
}

const ARABIC_INDIC_ZERO = 0x0660;
const EASTERN_ARABIC_INDIC_ZERO = 0x06f0;

/** "٠٥٠١٢٣٤٥٦٧" and "۰۵۰۱۲۳۴۵۶۷" -> "0501234567" */
export function toWesternDigits(value: string): string {
  return value.replace(/[٠-٩۰-۹]/g, char => {
    const code = char.charCodeAt(0);
    const zero =
      code >= EASTERN_ARABIC_INDIC_ZERO
        ? EASTERN_ARABIC_INDIC_ZERO
        : ARABIC_INDIC_ZERO;
    return String(code - zero);
  });
}

/**
 * Whether the number is a real one that can receive an SMS. The same rule the
 * server applies before it sends a one-time code.
 */
export function isMobileNumber(
  utils: PhoneNumberUtils,
  number: string,
  iso2?: string,
): boolean {
  if (!number || !utils.isValidNumber(number, iso2)) {
    return false;
  }
  const type = utils.getNumberType(number, iso2);
  return (
    type === utils.numberType.MOBILE ||
    type === utils.numberType.FIXED_LINE_OR_MOBILE
  );
}

/**
 * Works out the country of a number typed WITHOUT a "+" when it is not the
 * country selected in the field, so the flag can follow what the person typed
 * instead of the number silently becoming a wrong one ("0912345678" under the
 * Saudi flag is a Sudanese mobile, not +966 912 345 678).
 *
 * Meant for a number the person has finished typing (they left the field or
 * pressed Enter), never for one still being typed: the first nine digits of
 * "966501234567" are a complete Sudanese mobile.
 *
 * Returns null whenever the field should be left alone:
 *  - the number is typed as international ("+..." or "00..."): the field reads
 *    the country from the number itself;
 *  - it is a real number in the selected country, whatever its type. A Riyadh
 *    landline stays Saudi even though the same digits are a Sudanese mobile -
 *    what the person sees on the flag decides, never a guess;
 *  - no country is selected. The field always starts on one; the flag is
 *    only empty after the person typed the code of a country the field does
 *    not offer ("+971..."), which the widget drops. The digits left over are
 *    not a local number of any country we could name, so nothing is guessed;
 *  - no other country fits, or more than one does.
 */
export function resolveTypedPhone({
  raw,
  selected,
  candidates,
  dialCodes,
  utils,
}: ResolveOptions): ResolvedPhone | null {
  const typed = toWesternDigits(raw).trim();
  const digits = typed.replace(/\D+/g, '');
  if (!digits) {
    return null;
  }

  if (/^[^0-9]*\+/.test(typed) || digits.startsWith('00')) {
    return null;
  }

  if (!selected || utils.isValidNumber(digits, selected)) {
    return null;
  }

  const others = candidates.filter(iso2 => iso2 && iso2 !== selected);

  // a country code typed without the "+" ("966501234567"). The field may
  // have put a local "0" in front of it while it was being typed.
  const withoutZeros = digits.replace(/^0+/, '');
  for (const iso2 of others) {
    const dialCode = dialCodes[iso2];
    if (
      dialCode &&
      withoutZeros.startsWith(dialCode) &&
      isMobileNumber(utils, `+${withoutZeros}`)
    ) {
      return {iso2, e164: `+${withoutZeros}`};
    }
  }

  // a local number that is a mobile in exactly one other country
  const matches = others.filter(iso2 => isMobileNumber(utils, digits, iso2));
  if (matches.length === 1) {
    return {
      iso2: matches[0],
      e164: utils.formatNumber(digits, matches[0], utils.numberFormat.E164),
    };
  }

  return null;
}

/** Trimmed, lower-case ISO codes without blanks or repeats. */
export function normalizeCountries(countries?: string[] | null): string[] {
  const seen = new Set<string>();
  (countries ?? []).forEach(country => {
    const iso2 = `${country ?? ''}`.trim().toLowerCase();
    if (/^[a-z]{2}$/.test(iso2)) {
      seen.add(iso2);
    }
  });
  return [...seen];
}
